import { Link } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import { Alert, Button, Card, Empty, ErrorBox, Loading, PageHeader, useToast } from "../ui";
import { DeleteRecordButton } from "../components";
import { dateTime } from "../format";

export default function Followups() {
  const q = useQuery({ queryKey: ["followups"], queryFn: () => api.get("/followups") });
  const qc = useQueryClient();
  const toast = useToast();
  const act = useMutation({
    mutationFn: (v: { kind: string; refId: string; action: "done" | "snoozed" }) => api.post("/followups", v),
    onSuccess: (_r, v) => { qc.invalidateQueries({ queryKey: ["followups"] }); toast("success", v.action === "done" ? "Marked done" : "Snoozed for 3 days"); },
    onError: (e) => toast("error", (e as Error).message),
  });
  const copy = async (t: string) => { try { await navigator.clipboard.writeText(t); toast("success", "Message copied"); } catch { toast("error", "Copy failed — select the text manually"); } };
  if (q.isLoading) return <Loading />;
  if (q.isError) return <ErrorBox error={q.error} retry={() => q.refetch()} />;
  const total = q.data.groups.reduce((a: number, g: any) => a + g.items.length, 0);
  return (
    <>
      <PageHeader title="Follow-ups" sub={`${total} to look at`} />
      <Alert kind="info">{q.data.note}</Alert>
      <div className="stack" style={{ marginTop: 16 }}>
        {q.data.groups.map((g: any) => (
          <Card key={g.kind} title={`${g.title} (${g.items.length})`} pad={false}>
            {!g.items.length ? <Empty title="Nothing to chase" /> : g.items.map((i: any) => (
              <div key={i.refId} className="card-body" style={{ borderBottom: "1px solid var(--grey-100)" }}>
                <div className="row"><Link to={i.link}><strong>{i.who}</strong></Link><span className="muted small">{i.detail}</span><span className="spacer" />{i.phone && <a className="btn btn-sm btn-secondary" href={`tel:${i.phone.replace(/\s/g, "")}`}>Call {i.phone}</a>}</div>
                <div className="row" style={{ marginTop: 8, alignItems: "flex-start" }}>
                  <textarea className="input" readOnly rows={2} value={i.draft} style={{ flex: 1, minHeight: 60 }} onFocus={(e) => e.target.select()} aria-label="Draft message" />
                  <div className="stack" style={{ minWidth: 120 }}>
                    <Button size="sm" onClick={() => copy(i.draft)}>Copy message</Button>
                    <Button size="sm" variant="primary" onClick={() => act.mutate({ kind: g.kind, refId: i.refId, action: "done" })}>Done</Button>
                    <Button size="sm" variant="ghost" onClick={() => act.mutate({ kind: g.kind, refId: i.refId, action: "snoozed" })}>Snooze 3 days</Button>
                  </div>
                </div>
              </div>
            ))}
          </Card>
        ))}
        <RecentNotes />
      </div>
    </>
  );
}

const NOTE_KIND: Record<string, string> = { enquiry: "Enquiry", quote: "Quote", invoice: "Invoice", collection: "Collection", service: "Service reminder", review: "Review request" };

/** Items marked done or snoozed disappear from the lists above; removing a note brings the item back. */
function RecentNotes() {
  const q = useQuery({ queryKey: ["followup-notes"], queryFn: () => api.get("/followups/notes") });
  const qc = useQueryClient();
  const notes = q.data?.notes ?? [];
  if (!notes.length) return null;
  return (
    <Card title="Recently done or snoozed">
      {notes.map((n: any) => <div key={n.id} className="row" style={{ justifyContent: "space-between", marginBottom: 8 }}>
        <div><strong>{NOTE_KIND[n.kind] ?? n.kind}</strong> · {n.action === "done" ? "done" : `snoozed until ${n.until}`}<div className="small muted">{dateTime(n.at)}{n.by_name ? ` · ${n.by_name}` : ""}{n.note ? ` · ${n.note}` : ""}</div></div>
        <DeleteRecordButton kind="followup" id={n.id} label="Remove" onDone={() => { qc.invalidateQueries({ queryKey: ["followups"] }); qc.invalidateQueries({ queryKey: ["followup-notes"] }); }} />
      </div>)}
    </Card>
  );
}
